import type { Role } from '@pirouette/shared';
import type { IntentCategory } from './intents';

// Guided "how do I…" recipes for the command bar.
//
// A recipe is a deterministic, read-only checklist that walks the user through
// a multi-screen workflow. Each step EXPLAINS what to do and (optionally) links
// to a REAL route where the user performs the action themselves. Recipes NEVER
// auto-perform destructive or state-changing actions — they navigate and teach.
//
// Routes referenced here are verified against apps/web/src/routes/*. Steps with
// no `route` are pure guidance (e.g. "review the result").

export interface RecipeStep {
  /** Short imperative title, e.g. "Create the recital". */
  title: string;
  /** One or two sentences explaining what to do on this step. */
  detail: string;
  /**
   * Optional REAL route this step navigates to. Omit for explanation-only
   * steps. Never a destructive action — the user acts on the page.
   */
  route?: string;
}

export interface GuidedRecipe {
  /** Stable id — referenced by Intent.recipeId and used for persistence/keys. */
  id: string;
  /** Plain-language goal, phrased as the user would ask it. */
  title: string;
  /** One-line summary of the outcome. */
  description: string;
  category: IntentCategory;
  /** Roles allowed to run this recipe (mirrors the routes it touches). */
  requiredRole: Role[];
  /** Ordered steps. The first step with a `route` is the recipe's entry point. */
  steps: RecipeStep[];
}

export const RECIPES: GuidedRecipe[] = [
  {
    id: 'setup-recital',
    title: 'Set up my recital',
    description: 'Create a production, add shows, and build the lineup.',
    category: 'productions',
    requiredRole: ['admin'],
    steps: [
      {
        title: 'Create the production',
        detail:
          'Give your recital a name and date. This becomes the container for every show and dance.',
        route: '/admin/productions/new',
      },
      {
        title: 'Add shows and the lineup',
        detail: 'Open the production to add show times and arrange the running order of dances.',
        route: '/admin/productions',
      },
      {
        title: 'Configure recital packages',
        detail: 'Set up the fee packages (tickets, costumes, media) families will be charged for.',
        route: '/admin/recital-packages',
      },
      {
        title: 'Announce it to families',
        detail: 'Send a broadcast so families know dates, tickets, and what to expect.',
        route: '/admin/broadcasts',
      },
    ],
  },
  {
    id: 'charge-family',
    title: 'Charge a family',
    description: 'Record a new charge against a family account.',
    category: 'billing',
    requiredRole: ['admin'],
    steps: [
      {
        title: 'Find the family',
        detail: 'Open billing and confirm the family and their current balance first.',
        route: '/admin/billing',
      },
      {
        title: 'Create the charge',
        detail:
          'Add a new charge with the amount, category, and due date. Review it before saving — nothing is charged until you confirm.',
        route: '/admin/billing/new',
      },
    ],
  },
  {
    id: 'import-roster',
    title: 'Import my roster',
    description: 'Bring families and dancers in from a CSV.',
    category: 'people',
    requiredRole: ['admin'],
    steps: [
      {
        title: 'Import families',
        detail:
          'Upload your family CSV. You can map columns and preview rows before anything is created.',
        route: '/admin/families/import',
      },
      {
        title: 'Import dancers',
        detail: 'Upload the dancer CSV and link each dancer to their family.',
        route: '/admin/dancers/import',
      },
      {
        title: 'Spot-check the roster',
        detail: 'Open families and dancers to confirm the import looks right.',
        route: '/admin/families',
      },
    ],
  },
  {
    id: 'create-season',
    title: 'Create a new season',
    description: 'Set up a program/season and its classes.',
    category: 'classes',
    requiredRole: ['admin'],
    steps: [
      {
        title: 'Create the season',
        detail: 'Name the program/season and set its start and end dates.',
        route: '/admin/programs/new',
      },
      {
        title: 'Add classes',
        detail: 'Create the classes that belong to this season, with days and times.',
        route: '/admin/classes/new',
      },
      {
        title: 'Review the schedule',
        detail: 'Open classes to confirm the new season is laid out correctly.',
        route: '/admin/classes',
      },
    ],
  },
  {
    id: 'convert-trial',
    title: 'Convert a trial to enrollment',
    description: 'Turn a lead into an enrolled family.',
    category: 'people',
    requiredRole: ['admin'],
    steps: [
      {
        title: 'Open leads',
        detail: 'Find the trial or prospect you want to convert in the leads list.',
        route: '/admin/leads',
      },
      {
        title: 'Create the family',
        detail: 'Set them up as a family record with guardian and dancer details.',
        route: '/admin/families/new',
      },
      {
        title: 'Enroll in a class',
        detail: 'Add the dancer to the right class so their schedule and billing begin.',
        route: '/admin/classes',
      },
    ],
  },
  {
    id: 'setup-sms',
    title: 'Set up SMS',
    description: 'Turn on text-message announcements to families.',
    category: 'communication',
    requiredRole: ['admin'],
    steps: [
      {
        title: 'Open SMS settings',
        detail:
          'Review your A2P 10DLC status, compliance checklist, and disabled-by-default send gate.',
        route: '/admin/settings/sms',
      },
      {
        title: 'Use email while SMS is gated',
        detail:
          'Until registration, terms, and recipient consent are complete, use email/in-app broadcasts instead of SMS.',
        route: '/admin/broadcasts',
      },
    ],
  },
  {
    id: 'export-quickbooks',
    title: 'Export to QuickBooks',
    description: 'Download a CSV of charges for your accountant.',
    category: 'billing',
    requiredRole: ['admin'],
    steps: [
      {
        title: 'Open the billing export',
        detail: 'Go to the billing export tool.',
        route: '/admin/billing/export',
      },
      {
        title: 'Pick a date range and download',
        detail: 'Choose the from/to dates, then download the CSV and import it into QuickBooks.',
        route: '/admin/billing/export',
      },
    ],
  },
  {
    id: 'costume-measurements',
    title: 'Add costume measurements',
    description: 'Record costume sizes on dancer profiles.',
    category: 'people',
    requiredRole: ['admin'],
    steps: [
      {
        title: 'Open dancers',
        detail: 'Find the dancer whose costume measurements you want to record.',
        route: '/admin/dancers',
      },
      {
        title: 'Open the dancer profile',
        detail: 'On the dancer detail page, enter their costume measurements and save.',
        route: '/admin/dancers',
      },
    ],
  },
  {
    id: 'take-attendance',
    title: 'Take attendance',
    description: 'Mark who showed up for a class.',
    category: 'classes',
    requiredRole: ['faculty', 'admin'],
    steps: [
      {
        title: 'Open your classes',
        detail: 'Pick the class you are teaching now.',
        route: '/faculty/classes',
      },
      {
        title: 'Mark attendance',
        detail:
          'Open the class to its attendance view and check off each dancer as present, absent, or late.',
        route: '/faculty/classes',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

/** Find a recipe by id, or undefined. */
export function getRecipe(id: string): GuidedRecipe | undefined {
  return RECIPES.find((recipe) => recipe.id === id);
}

/** Whether the role may run the recipe. */
export function recipeVisibleToRole(recipe: GuidedRecipe, role: Role | null | undefined): boolean {
  if (!role) return false;
  return recipe.requiredRole.includes(role);
}

/** All recipes the role may run, in declaration order. */
export function recipesForRole(role: Role | null | undefined): GuidedRecipe[] {
  return RECIPES.filter((recipe) => recipeVisibleToRole(recipe, role));
}

/** The entry-point route of a recipe — the first step that navigates. */
export function recipeEntryRoute(recipe: GuidedRecipe): string | undefined {
  return recipe.steps.find((step) => step.route)?.route;
}
